import { Events } from '../events';
import type { StudioPublishResult } from '../studio-publish';

// "Publish to Studio" dialog. Packages the current scene using the real
// export pipeline (compressed .ply + settings.json, matching the actual
// @playcanvas/supersplat-viewer schema used by studio.simamdigital.com) and
// registers a shared scene record so it shows up in the Studio dashboard.
// Reuses the same plain-DOM overlay approach as ui/share-dialog.ts.
const registerStudioPublishDialog = (events: Events) => {
    let root: HTMLElement = null;

    const close = () => {
        if (root) {
            root.remove();
            root = null;
        }
    };

    const field = (labelText: string, value: string): HTMLElement => {
        const wrap = document.createElement('div');
        wrap.className = 'share-field';

        const label = document.createElement('div');
        label.className = 'share-field-label';
        label.textContent = labelText;
        wrap.appendChild(label);

        const rowEl = document.createElement('div');
        rowEl.className = 'share-field-row';

        const input = document.createElement('input');
        input.className = 'share-input';
        input.readOnly = true;
        input.value = value;
        rowEl.appendChild(input);

        const copyBtn = document.createElement('button');
        copyBtn.className = 'share-btn share-btn-copy';
        copyBtn.textContent = 'Copy';
        copyBtn.addEventListener('click', async () => {
            try {
                await navigator.clipboard.writeText(value);
            } catch (e) {
                input.select();
                document.execCommand('copy');
            }
            copyBtn.textContent = 'Copied';
            setTimeout(() => {
                copyBtn.textContent = 'Copy';
            }, 1400);
        });
        rowEl.appendChild(copyBtn);

        wrap.appendChild(rowEl);
        return wrap;
    };

    const renderResult = (body: HTMLElement, result: StudioPublishResult) => {
        body.innerHTML = '';

        const meta = document.createElement('div');
        meta.className = 'share-meta';
        meta.textContent = 'Published to Studio.';
        body.appendChild(meta);

        const links = document.createElement('div');
        links.className = 'share-links';
        links.appendChild(field('Studio viewer link', result.viewerUrl));

        const openRow = document.createElement('div');
        openRow.className = 'share-actions';
        const openBtn = document.createElement('button');
        openBtn.className = 'share-btn share-btn-primary';
        openBtn.textContent = 'Open in Studio';
        openBtn.addEventListener('click', () => window.open(result.viewerUrl, '_blank')?.focus());
        openRow.appendChild(openBtn);
        links.appendChild(openRow);

        links.appendChild(field('Re-open in Editor', result.editorUrl));

        body.appendChild(links);
    };

    events.function('show.studioPublishDialog', async () => {
        close();

        root = document.createElement('div');
        root.id = 'share-dialog';
        root.addEventListener('pointerdown', (e) => {
            if (e.target === root) {
                close();
            }
        });

        const panel = document.createElement('div');
        panel.className = 'share-panel';

        const header = document.createElement('div');
        header.className = 'share-header';
        const title = document.createElement('div');
        title.className = 'share-title';
        title.textContent = 'Publish to Studio';
        header.appendChild(title);
        const closeBtn = document.createElement('div');
        closeBtn.className = 'share-close';
        closeBtn.textContent = '×';
        closeBtn.addEventListener('click', close);
        header.appendChild(closeBtn);
        panel.appendChild(header);

        const body = document.createElement('div');
        body.className = 'share-body';
        panel.appendChild(body);

        root.appendChild(panel);
        document.body.appendChild(root);

        if (!events.invoke('studio.configured')) {
            const note = document.createElement('p');
            note.className = 'share-note';
            note.textContent = 'Studio publishing isn’t configured yet. Deploy the publishScene function in the studio repo, then set studioPublishFunctionUrl/studioPublishToken in src/firebase-config.ts.';
            body.appendChild(note);
            return;
        }

        const docName = (events.invoke('doc.name') as string) || 'Untitled Scene';

        const intro = document.createElement('p');
        intro.className = 'share-note';
        intro.appendChild(document.createTextNode('Publish '));
        const nameEl = document.createElement('strong');
        nameEl.textContent = docName;
        intro.appendChild(nameEl);
        intro.appendChild(document.createTextNode(
            ' to Studio so it appears in the studio.simamdigital.com dashboard with a real, client-shareable viewer link.'
        ));
        body.appendChild(intro);

        const actions = document.createElement('div');
        actions.className = 'share-actions';
        const publishBtn = document.createElement('button');
        publishBtn.className = 'share-btn share-btn-primary';
        publishBtn.textContent = 'Publish to Studio';
        actions.appendChild(publishBtn);
        body.appendChild(actions);

        publishBtn.addEventListener('click', async () => {
            publishBtn.disabled = true;
            publishBtn.textContent = 'Publishing…';
            try {
                const result = await events.invoke('studio.publish', { title: docName }) as StudioPublishResult;
                renderResult(body, result);
            } catch (err) {
                const msg = document.createElement('p');
                msg.className = 'share-error';
                msg.textContent = `Publish failed: ${(err as Error)?.message ?? err}`;
                body.appendChild(msg);
                publishBtn.disabled = false;
                publishBtn.textContent = 'Try again';
            }
        });
    });
};

export { registerStudioPublishDialog };
